import React, { useEffect, useState, useRef } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

export default function PaymentCreate() {
    const navigate = useNavigate();
    const { settings } = useSelector((state) => state.Home);
    const [card, setCard] = useState(null);
    const [payments, setPayments] = useState(null);
    const [showCardDetails, setShowCardDetails] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const cardContainerRef = useRef(null);

    const applicationId = "sandbox-sq0idb-ciK-AXiZD_0vLxk2-blcwA";
    const locationId = "LDF3D892Z9CYF";

    useEffect(() => {
        const initializeSquarePaymentForm = async () => {
            if (!window.Square) {
                console.error("Square SDK not available.");
                return;
            }

            try {
                const paymentsInstance = window.Square.payments(applicationId, locationId);
                setPayments(paymentsInstance);
                const cardInstance = await paymentsInstance.card();
                
                if (cardContainerRef.current) {
                    cardContainerRef.current.innerHTML = '';
                }

                await cardInstance.attach('#card-container');
                setCard(cardInstance);
            } catch (error) {
                console.error("Error initializing Square Payments:", error);
                toast.error("Failed to initialize payment system");
            }
        };

        if (showCardDetails) {
            initializeSquarePaymentForm();
        }

        return () => {
            if (cardContainerRef.current) {
                cardContainerRef.current.innerHTML = '';
            }
        };
    }, [showCardDetails]);

    const validationSchema = Yup.object({
        currency_code: Yup.string().required("Currency is required"),
        other_currency_code: Yup.string().when("currency_code", {
            is: (val) => val === "Other",
            then: (schema) => schema.required("Please select a currency type"),
            otherwise: (schema) => schema.notRequired(),
        }),
        name_title: Yup.string().required("Title is required"),
        name: Yup.string().required("Name is required"),
        email: Yup.string().email("Invalid email format").required("Email is required"),
        phone: Yup.string()
            .matches(/^[0-9]{10}$/, "Phone number must be 10 digits")
            .required("Phone is required"),
        city: Yup.string().required("City is required"),
        state: Yup.string().required("State is required"),
        zip: Yup.string().required("Zip is required"),
        country: Yup.string().required("Country is required"),
        journal_name: Yup.string().required("Journal Name is required"),
        article_title: Yup.string().required("Title of the article is required"),
        invoice_number: Yup.string().required("Invoice Number is required"),
        address: Yup.string().required("Address is required"),
        amount: Yup.number()
            .required("Chosen Price is required")
            .positive("Amount must be positive"),
    });

    const formik = useFormik({
        initialValues: {
            currency_code: "",
            other_currency_code: '',
            name_title: "",
            name: "",
            email: "",
            phone: "",
            city: "",
            state: "",
            zip: "",
            country: "",
            journal_name: "",
            article_title: "",
            invoice_number: "",
            address: "",
            amount: ""
        },
        validationSchema,
        onSubmit: async (values) => {
            try {
                const payload = {
                    ...values,
                    currency_code: values.currency_code === "Other" ? values.other_currency_code : values.currency_code
                };
                delete payload.other_currency_code;

                console.log("Sending payload:", payload);
                const response = await axios.post(`${process.env.REACT_APP_URL}/payment/create`, payload);
                console.log("Payment Created:", response.data);
                
                // Navigate to make-payment page with the response data
                navigate('/make-payment', { 
                    state: { 
                        paymentData: response.data.data || response.data,
                        formData: values
                    } 
                });
            } catch (error) {
                console.error("Payment creation error:", error.response?.data);
                toast.error(error?.response?.data?.message ? error?.response?.data?.message : error?.response?.data || "Something Went Wrong");
            }
        },
    });

    const handleSubmitPayment = async () => {
        if (!card || !payments) {
            toast.error("Payment system not ready yet");
            return;
        }

        setIsProcessing(true);

        try {
            const tokenResult = await card.tokenize();
            
            if (tokenResult.status !== "OK") {
                console.error("Tokenization failed:", tokenResult.errors);
                toast.error("Please check your card details and try again.");
                return;
            }

            const token = tokenResult.token;

            // Proceed with payment
            const proceedPayload = {
                token: token,
                payment_uuid: formik.values.uuid
            };

            console.log("Sending proceed payload:", proceedPayload);

            const proceedResponse = await axios.post(`${process.env.REACT_APP_URL}/payment/proceed`, proceedPayload);
            console.log("Proceed payment response:", proceedResponse.data);

            toast.success("Payment Processed Successfully!");
            formik.resetForm();
            setShowCardDetails(false);
            navigate('/make-payment');
        } catch (error) {
            console.error("Full error object:", error);
            console.error("Error response:", error.response);
            console.error("Error message:", error.message);
            
            if (error.response) {
                toast.error(error.response.data?.message || error.response.data || "Server error occurred");
            } else if (error.request) {
                toast.error("No response from server. Please check your connection.");
            } else {
                toast.error("Error processing payment. Please try again.");
            }
        } finally {
            setIsProcessing(false);
        }
    };

    // const countryCurrencyList = [
    //     { country: "Afghanistan", currency: "AFN" },
    //     { country: "Albania", currency: "ALL" },
    //     { country: "Algeria", currency: "DZD" },
    //     { country: "Argentina", currency: "ARS" },
    //     { country: "Australia", currency: "AUD" },
    //     { country: "Austria", currency: "EUR" },
    //     { country: "Bangladesh", currency: "BDT" },
    //     { country: "Belgium", currency: "EUR" },
    //     { country: "Brazil", currency: "BRL" },
    //     { country: "Canada", currency: "CAD" },
    //     { country: "China", currency: "CNY" },
    //     { country: "Denmark", currency: "DKK" },
    //     { country: "Egypt", currency: "EGP" },
    //     { country: "France", currency: "EUR" },
    //     { country: "Germany", currency: "EUR" },
    //     { country: "India", currency: "INR" },
    //     { country: "Indonesia", currency: "IDR" },
    //     { country: "Japan", currency: "JPY" },
    //     { country: "Kenya", currency: "KES" },
    //     { country: "Kuwait", currency: "KWD" },
    //     { country: "Malaysia", currency: "MYR" },
    //     { country: "Nepal", currency: "NPR" },
    //     { country: "Netherlands", currency: "EUR" },
    //     { country: "New Zealand", currency: "NZD" },
    //     { country: "Nigeria", currency: "NGN" },
    //     { country: "Norway", currency: "NOK" },
    //     { country: "Oman", currency: "OMR" },
    //     { country: "Pakistan", currency: "PKR" },
    //     { country: "Philippines", currency: "PHP" },
    //     { country: "Qatar", currency: "QAR" },
    //     { country: "Russia", currency: "RUB" },
    //     { country: "Saudi Arabia", currency: "SAR" },
    //     { country: "Singapore", currency: "SGD" },
    //     { country: "South Africa", currency: "ZAR" },
    //     { country: "Sri Lanka", currency: "LKR" },
    //     { country: "Sweden", currency: "SEK" },
    //     { country: "Switzerland", currency: "CHF" },
    //     { country: "Thailand", currency: "THB" },
    //     { country: "Turkey", currency: "TRY" },
    //     { country: "UAE", currency: "AED" },
    //     { country: "UK", currency: "GBP" },
    //     { country: "USA", currency: "USD" },
    //     { country: "Vietnam", currency: "VND" },
    // ];

    return (
        <div className="container p-5">
            <div className="paymentform-container">
                <div style={{ backgroundColor: "rgb(0 0 0 / 3%)" }} >
                    <img
                        src={settings?.data?.color_logo_url}
                        crossOrigin="anonymous"
                        style={{
                            height: "auto",
                            width: "100px",
                            objectFit: "contain",
                            display: "block",
                            margin: "0 auto",
                        }}
                        alt="Logo"
                    />
                    <div style={{ textAlign: "center" }}>www.empiricaljournals.com</div>
                    <hr />
                </div>
                <form className="payment-form" onSubmit={formik.handleSubmit}>
                    <fieldset>
                        <p>Note: * Fields are mandatory</p>
                        <div className="row">
                            <div className="form-group">
                                <p>
                                    Choose Currency *:&nbsp;
                                    <input
                                        name="currency_code"
                                        type="radio"
                                        value="USD"
                                        onChange={formik.handleChange}
                                        checked={formik.values.currency_code === "USD"}
                                    /> USD ($) &nbsp;
                                    {/* <input
                                        name="currency_code"
                                        type="radio"
                                        value="EUR"
                                        onChange={formik.handleChange}
                                        checked={formik.values.currency_code === "EUR"}
                                    /> EUR (€) &nbsp;
                                    <input
                                        name="currency_code"
                                        type="radio"
                                        value="Other"
                                        onChange={formik.handleChange}
                                        checked={formik.values.currency_code === "Other"}
                                    /> Other
                                    {formik.values.currency_code === "Other" && (
                                        <select
                                            name="other_currency_code"
                                            onChange={formik.handleChange}
                                            value={formik.values.other_currency_code}
                                            className="form-control"
                                            style={{ display: 'inline-block', width: 'auto', marginLeft: '10px' }}
                                        >
                                            <option value="">Select Currency</option>
                                            {countryCurrencyList.map((item, index) => (
                                                <option key={index} value={item.currency}>
                                                    {item.country} - {item.currency}
                                                </option>
                                            ))}
                                        </select>
                                    )} */}
                                </p>
                                {formik.touched.currency_code && formik.errors.currency_code && (
                                    <div className="error">{formik.errors.currency_code}</div>
                                )}
                                {formik.values.currency_code === "Other" && formik.touched.other_currency_code && formik.errors.other_currency_code && (
                                    <div className="error">{formik.errors.other_currency_code}</div>
                                )}
                            </div>
                        </div>

                        <div className="row">
                            <div className="col">
                                <div className="form-group">
                                    <label>Title *</label>
                                    <select
                                        name="name_title"
                                        value={formik.values.name_title}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    >
                                        <option value="">--Select Title--</option>
                                        <option value="Mr">Mr</option>
                                        <option value="Ms">Ms</option>
                                        <option value="Mrs">Mrs</option>
                                        <option value="Dr">Dr</option>
                                        <option value="Prof">Prof</option>
                                    </select>
                                    <div className="text-danger">
                                        {formik.touched.name_title && formik.errors.name_title}
                                    </div>
                                </div>
                            </div>
                            <div className="col">
                                <div className="form-group">
                                    <label>Name *</label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formik.values.name}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                    <div className="text-danger">
                                        {formik.touched.name && formik.errors.name}
                                    </div>
                                </div>
                            </div>
                            <div className="col">
                                <div className="form-group">
                                    <label>E-mail *</label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formik.values.email}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                    <div className="text-danger">
                                        {formik.touched.email && formik.errors.email}
                                    </div>
                                </div>
                            </div>
                            <div className="col">
                                <div className="form-group">
                                    <label>Phone *</label>
                                    <input
                                        type="text"
                                        name="phone"
                                        value={formik.values.phone}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                    <div className="text-danger">
                                        {formik.touched.phone && formik.errors.phone}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="row">
                            <div className="col">
                                <div className="form-group">
                                    <label>City *</label>
                                    <input
                                        type="text"
                                        name="city"
                                        value={formik.values.city}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                    <div className="text-danger">
                                        {formik.touched.city && formik.errors.city}
                                    </div>
                                </div>
                            </div>
                            <div className="col">
                                <div className="form-group">
                                    <label>State *</label>
                                    <input
                                        type="text"
                                        name="state"
                                        value={formik.values.state}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                    <div className="text-danger">
                                        {formik.touched.state && formik.errors.state}
                                    </div>
                                </div>
                            </div>
                            <div className="col">
                                <div className="form-group">
                                    <label>Zip *</label>
                                    <input
                                        type="text"
                                        name="zip"
                                        value={formik.values.zip}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                    <div className="text-danger">
                                        {formik.touched.zip && formik.errors.zip}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="row">
                            <div className="col">
                                <div className="form-group">
                                    <label>Address *</label>
                                    <textarea
                                        name="address"
                                        value={formik.values.address}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                    <div className="text-danger">
                                        {formik.touched.address && formik.errors.address}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="row">
                            <div className="col">
                                <div className="form-group">
                                    <label>Country *</label>
                                    <input
                                        type="text"
                                        name="country"
                                        value={formik.values.country}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                    <div className="text-danger">
                                        {formik.touched.country && formik.errors.country}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="row">
                            <div className="col">
                                <div className="form-group">
                                    <label>Journal Name *</label>
                                    <input
                                        type="text"
                                        name="journal_name"
                                        value={formik.values.journal_name}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                    <div className="text-danger">
                                        {formik.touched.journal_name && formik.errors.journal_name}
                                    </div>
                                </div>
                            </div>
                            <div className="col">
                                <div className="form-group">
                                    <label>Title of the article *</label>
                                    <input
                                        type="text"
                                        name="article_title"
                                        value={formik.values.article_title}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                    <div className="text-danger">
                                        {formik.touched.article_title && formik.errors.article_title}
                                    </div>
                                </div>
                            </div>
                            <div className="col">
                                <div className="form-group">
                                    <label>Invoice Number *</label>
                                    <input
                                        type="text"
                                        name="invoice_number"
                                        value={formik.values.invoice_number}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                    <div className="text-danger">
                                        {formik.touched.invoice_number && formik.errors.invoice_number}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="row">
                            <div className="col">
                                <div className="form-group">
                                    <label>Chosen Price *</label>
                                    <input
                                        type="text"
                                        name="amount"
                                        value={formik.values.amount}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                    <div className="text-danger">
                                        {formik.touched.amount && formik.errors.amount}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </fieldset>

                    <div className="col-lg-4 col-md-4 justify-content-center align-items-center">
                        <button className="btn btn-primary" type="submit">
                            Proceed to Pay
                        </button>
                    </div>
                </form>

                {showCardDetails && (
                    <div className="card-details-section" style={{ marginTop: '20px', padding: '20px', border: '1px solid #ddd', borderRadius: '4px' }}>
                        <h4>Card Details</h4>
                        <div id="card-container" ref={cardContainerRef} style={{ 
                            height: '100px',
                            marginBottom: '1rem',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            padding: '10px'
                        }}></div>
                        <button 
                            className="btn btn-primary" 
                            onClick={handleSubmitPayment}
                            disabled={isProcessing}
                        >
                            {isProcessing ? "Processing..." : "Submit Payment"}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
